import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import Lenis from 'lenis';
import { ArrowLeft } from 'lucide-react';
import { MonkeyLogo } from '../components/MonkeyLogo';

const DISPLAY = "font-['Cabinet_Grotesk',_'Inter',_sans-serif] font-black tracking-tighter";
const BODY = "font-['Satoshi',_'Inter',_sans-serif]";
const EASE_OUT = [0.16, 1, 0.3, 1];
const EFFECTIVE = 'February 21, 2026';

const SECTIONS = [
  {
    h: '1. Who is in charge of your data',
    p: `LinkMonkey ("we", "us") is the data controller for the personal data we process about you when you use our link‑in‑bio service. You can reach us at privacy@linkmonkey.io for any data‑protection question or request.`,
  },
  {
    h: '2. What we collect',
    children: (
      <ul className="list-disc list-outside ml-5 space-y-2.5 text-white/65 leading-relaxed text-[15px] md:text-base">
        <li><strong className="text-white/85">Account data:</strong> email, hashed password, username, display name, bio, avatar and header images.</li>
        <li><strong className="text-white/85">Profile content:</strong> the links you add, themes and customisation you choose, fan‑page posts, tip‑jar PayPal email, email‑campaign drafts and history.</li>
        <li><strong className="text-white/85">Billing data:</strong> Stripe customer ID, subscription status, billing cycle, the last four digits of your card (stored by Stripe, not us) and invoice history.</li>
        <li><strong className="text-white/85">Visitor analytics:</strong> page views, link clicks, approximate visitor country (IP‑geolocated) and referrer. Visitor IP addresses are used only momentarily for country lookup and rate‑limiting, then discarded.</li>
        <li><strong className="text-white/85">Communication logs:</strong> password‑reset, double opt‑in, failed payment and account‑deletion confirmation emails we send you via Resend.</li>
        <li><strong className="text-white/85">Operational logs:</strong> security events (login attempts, lockouts, token rotations), webhook errors and admin actions, retained for fraud/abuse defence.</li>
      </ul>
    ),
  },
  {
    h: '3. Why we process it',
    p: `We process the data above to (a) operate the service — render your public profile, store your uploads, deliver the emails you send, charge subscriptions; (b) keep the service safe — rate‑limit logins, detect abuse, prevent fraud, comply with our legal obligations; (c) improve the service — aggregate (non‑identifying) analytics about which features are used; (d) communicate with you about your account, security alerts and billing. We do not sell your data, ever.`,
  },
  {
    h: '4. Legal basis (GDPR)',
    p: `For EU/UK users: we rely on contract (Art. 6(1)(b)) to deliver the service you signed up for, legitimate interests (Art. 6(1)(f)) for security, fraud prevention and aggregate analytics, legal obligation (Art. 6(1)(c)) for tax and accounting records, and consent (Art. 6(1)(a)) for optional features such as the newsletter signup widget where we collect a double opt‑in token before sending you anything.`,
  },
  {
    h: '5. Cookies and similar tech',
    children: (
      <ul className="list-disc list-outside ml-5 space-y-2.5 text-white/65 leading-relaxed text-[15px] md:text-base">
        <li><strong className="text-white/85">access_token, refresh_token</strong> (HttpOnly, Secure, SameSite=Lax) — keep you logged in. Required.</li>
        <li><strong className="text-white/85">csrf_token</strong> (Secure, SameSite=Lax) — protects you against cross‑site request forgery. Required.</li>
        <li>We do not use third‑party advertising or tracking cookies. We do not run pixels.</li>
      </ul>
    ),
  },
  {
    h: '6. Who we share data with',
    children: (
      <ul className="list-disc list-outside ml-5 space-y-2.5 text-white/65 leading-relaxed text-[15px] md:text-base">
        <li><strong className="text-white/85">Stripe (Ireland) Ltd.</strong> — payment processing, subscription management, Customer Portal. Subject to Stripe's Privacy Policy.</li>
        <li><strong className="text-white/85">Resend</strong> — outbound transactional and creator‑campaign emails. Subject to Resend's Privacy Policy.</li>
        <li><strong className="text-white/85">Emergent Object Storage</strong> — hosts the images you upload (avatar, header, custom wallpaper, fan‑page images).</li>
        <li><strong className="text-white/85">MongoDB Atlas</strong> — encrypted‑at‑rest database for everything else.</li>
        <li><strong className="text-white/85">Cloudflare</strong> — DDoS protection and CDN for static assets. May briefly process IP addresses for routing and abuse defence.</li>
        <li>We never sell or rent personal data, and we never use it to train AI models.</li>
      </ul>
    ),
  },
  {
    h: '7. International transfers',
    p: `Some of our processors are located outside the EU/EEA. Where transfers leave the EU/EEA we rely on the European Commission's Standard Contractual Clauses or an adequacy decision to provide an essentially equivalent level of protection.`,
  },
  {
    h: '8. How long we keep it',
    p: `Active account data is kept for as long as your account exists. When you delete your account (Settings → Delete account, or DELETE /api/auth/me), we permanently remove your profile, links, uploads, fan‑page posts and analytics within 30 days, and cancel your Stripe subscription immediately. Security and billing records may be retained for up to 7 years where required by law (e.g. VAT/accounting). Aggregate, non‑identifying analytics may be retained indefinitely.`,
  },
  {
    h: '9. Your rights',
    children: (
      <div className="space-y-4">
        <p className={`${BODY} text-white/65 leading-relaxed text-[15px] md:text-base`}>
          Under GDPR and similar laws, you have the right to:
        </p>
        <ul className="list-disc list-outside ml-5 space-y-2.5 text-white/65 leading-relaxed text-[15px] md:text-base">
          <li><strong className="text-white/85">Access</strong> — request a copy of the data we hold about you.</li>
          <li><strong className="text-white/85">Rectify</strong> — correct inaccurate or incomplete data.</li>
          <li><strong className="text-white/85">Erase</strong> — delete your account from Settings, or email privacy@linkmonkey.io.</li>
          <li><strong className="text-white/85">Restrict or object</strong> — to certain processing based on legitimate interest.</li>
          <li><strong className="text-white/85">Portability</strong> — receive your profile data in a structured, machine‑readable format.</li>
          <li><strong className="text-white/85">Withdraw consent</strong> — for anything we ask consent for (e.g. newsletter widget).</li>
          <li><strong className="text-white/85">Complain</strong> — to your local supervisory authority. In Ireland, that's the Data Protection Commission (dataprotection.ie).</li>
        </ul>
        <p className={`${BODY} text-white/65 leading-relaxed text-[15px] md:text-base`}>
          Email privacy@linkmonkey.io to exercise any of these rights. We respond within 30 days.
        </p>
      </div>
    ),
  },
  {
    h: '10. Security',
    p: `Passwords are hashed with bcrypt. Sessions use HttpOnly, Secure, SameSite cookies with a CSRF double‑submit token. Login attempts are rate‑limited per IP, and we rotate refresh tokens on every refresh with cascade revocation on reuse. Uploads are scanned for malicious content (magic‑byte verification) and served from a path‑sanitised proxy. We enforce a strict Content‑Security‑Policy, HSTS, and the standard suite of security headers. No system is perfectly secure — please tell us about anything you find by emailing security@linkmonkey.io.`,
  },
  {
    h: '11. Children',
    p: `LinkMonkey is not directed at people under 16. We don't knowingly collect data from anyone under 16. If you believe a child has signed up, email privacy@linkmonkey.io and we'll delete the account.`,
  },
  {
    h: '12. Changes',
    p: `We may update this Privacy Policy. Material changes will be announced by email or via a notice in the dashboard at least 14 days before they take effect. The "Effective" date at the top of this page always reflects the latest version.`,
  },
  {
    h: '13. Contact',
    p: `privacy@linkmonkey.io for data‑protection requests. support@linkmonkey.io for everything else. We read every message.`,
  },
];

export default function Privacy() {
  useEffect(() => {
    const lenis = new Lenis({ smoothWheel: true, lerp: 0.1 });
    function raf(time) { lenis.raf(time); requestAnimationFrame(raf); }
    requestAnimationFrame(raf);
    return () => lenis.destroy();
  }, []);

  return (
    <div className="min-h-screen bg-[#06070A] text-white" data-testid="privacy-page">
      <Helmet>
        <title>Privacy Policy — LinkMonkey</title>
        <meta name="description" content="LinkMonkey Privacy Policy. How we collect, use and protect your personal data." />
      </Helmet>

      <div className="fixed inset-0 pointer-events-none opacity-[0.04] bg-[url('data:image/svg+xml;utf8,<svg xmlns=%22http://www.w3.org/2000/svg%22 width=%22200%22 height=%22200%22><filter id=%22n%22><feTurbulence type=%22fractalNoise%22 baseFrequency=%220.85%22/></filter><rect width=%22100%25%22 height=%22100%25%22 filter=%22url(%23n)%22/></svg>')]" />
      <div className="fixed top-0 left-1/2 -translate-x-1/2 w-[120vw] h-[60vh] pointer-events-none"
           style={{ background: 'radial-gradient(ellipse at top, rgba(0,85,255,0.18) 0%, transparent 60%)' }} />

      <nav className="relative z-10 px-6 md:px-10 py-6 flex items-center justify-between max-w-7xl mx-auto">
        <Link to="/" className="flex items-center gap-3 group" data-testid="privacy-nav-home">
          <MonkeyLogo size={32} />
          <span className={`${BODY} text-base font-semibold text-white`}>LinkMonkey</span>
        </Link>
        <Link to="/" className={`${BODY} flex items-center gap-2 text-sm text-white/60 hover:text-white transition-colors`} data-testid="privacy-back-home">
          <ArrowLeft className="w-4 h-4" />
          Back home
        </Link>
      </nav>

      <header className="relative z-10 px-6 md:px-10 pt-12 md:pt-20 pb-10 max-w-3xl mx-auto">
        <motion.p
          initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, ease: EASE_OUT }}
          className={`${BODY} text-[11px] uppercase tracking-[0.3em] text-white/40 mb-5`}>
          Legal
        </motion.p>
        <motion.h1
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: EASE_OUT, delay: 0.05 }}
          className={`${DISPLAY} text-5xl md:text-7xl leading-[0.95] mb-6`}>
          Privacy <em className="italic font-light text-white/80">Policy</em>
        </motion.h1>
        <motion.p
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: EASE_OUT, delay: 0.1 }}
          className={`${BODY} text-white/55 text-base md:text-lg leading-relaxed max-w-2xl`}>
          What we collect, why we collect it, who we share it with, and the rights you have over it. No dark patterns. No tracking pixels. No selling your data — ever.
        </motion.p>
        <p className={`${BODY} text-xs text-white/30 mt-6 font-mono`}>Effective {EFFECTIVE}</p>
      </header>

      <main className="relative z-10 px-6 md:px-10 pb-24 max-w-3xl mx-auto">
        <div className="space-y-10">
          {SECTIONS.map((s, i) => (
            <motion.section
              key={s.h}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-80px' }}
              transition={{ duration: 0.5, ease: EASE_OUT, delay: Math.min(i * 0.02, 0.2) }}
              data-testid={`privacy-section-${i + 1}`}
            >
              <h2 className={`${DISPLAY} text-2xl md:text-3xl text-white mb-3`}>{s.h}</h2>
              {s.children ? s.children : (
                <p className={`${BODY} text-white/65 leading-relaxed text-[15px] md:text-base`}>{s.p}</p>
              )}
            </motion.section>
          ))}
        </div>

        <div className="mt-20 pt-10 border-t border-white/10 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <p className={`${BODY} text-sm text-white/40`}>
            Also read our{' '}
            <Link to="/terms" className="text-white/80 hover:text-white underline underline-offset-4" data-testid="privacy-link-to-terms">
              Terms of Service
            </Link>.
          </p>
          <Link to="/" className={`${BODY} text-sm text-white/50 hover:text-white transition-colors`}>
            ← Back to LinkMonkey
          </Link>
        </div>
      </main>
    </div>
  );
}
